import { v4 } from 'uuid';
import { type WorkflowNodeInput } from '@cotera/api';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { resolveViewerTimezone } from './debounce-timezone';
import type {
  DatasetTriggerCriterion,
  DatasetTriggerRunMode,
} from '@cotera/sdk/core';
import type { WorkflowGraphApiData } from './workflow-graph.types';
import type { WorkflowTriggerOption } from '../trigger/hooks';
import type { WorkflowWebhookProviderKey } from '@cotera/client/app/etc/api/typed-client';
import type { WebhookInstallStatus } from '@cotera/client/app/v1/webhook-providers/types';
import stableStringify from '@cotera/client/app/etc/stringify';
import { withForEachBodyDisplayFields } from './components/node/edit-strategies/for-each-child-node';
import { healWorkflowGraphBindingSources } from './heal-workflow-binding-sources';
import type { WorkflowClipboardPayload } from './workflow-node-clipboard';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

type DraftGraphContext = {
  workflowId: string;
  orgId: string;
  userId: string;
};

export type DraftWebhookCreateParams = {
  name: string;
  providerKey?: WorkflowWebhookProviderKey;
  verificationConfig?: unknown;
  eventKey?: string;
  eventConfig?: unknown;
  endpointSecret?: string;
};

export type WorkflowDraftState = {
  graph: WorkflowGraphApiData;
  pendingWebhookCreates: Map<string, DraftWebhookCreateParams>;
  idempotencyKey: PersistedWorkflow.WorkflowIdempotencyKey | null;
};

export function cloneWorkflowDraftState(
  state: WorkflowDraftState
): WorkflowDraftState {
  return {
    graph: structuredClone(state.graph),
    pendingWebhookCreates: new Map(state.pendingWebhookCreates),
    idempotencyKey: state.idempotencyKey,
  };
}

export function createDraftStateFromGraph(
  ctx: DraftGraphContext,
  graph: WorkflowGraphApiData,
  options?: {
    idempotencyKey?: PersistedWorkflow.WorkflowIdempotencyKey | null;
  }
): WorkflowDraftState {
  void ctx;
  // Persist only stamps childNodeId; derive body badge fields from the body node.
  const cloned = structuredClone(graph);
  // Legacy graphs bind by node display name; the save endpoint only accepts ids.
  // Heal on the way in so the editor shows (and re-saves) resolvable refs. Agent
  // titles aren't in the graph, so those aliases arrive later via
  // `WorkflowModel.healLegacyBindingSources`.
  return {
    graph: healWorkflowGraphBindingSources(
      withForEachBodyDisplayFields(cloned)
    ),
    pendingWebhookCreates: new Map(),
    idempotencyKey: options?.idempotencyKey ?? null,
  };
}

function serializeWorkflowDraftState(state: WorkflowDraftState): string {
  return JSON.stringify({
    graph: state.graph,
    pendingWebhookCreates: [...state.pendingWebhookCreates.entries()],
    idempotencyKey: state.idempotencyKey,
  });
}

export function workflowDraftStatesEqual(
  a: WorkflowDraftState,
  b: WorkflowDraftState
): boolean {
  return serializeWorkflowDraftState(a) === serializeWorkflowDraftState(b);
}

export function isWorkflowDraftDirtyAgainstBaseline(
  draft: WorkflowDraftState,
  baseline: WorkflowDraftState,
  name: string,
  baselineName: string
): boolean {
  if (name !== baselineName) {
    return true;
  }
  return !workflowDraftStatesEqual(draft, baseline);
}

export function createWorkflowDraftState(
  ctx: DraftGraphContext
): WorkflowDraftState {
  return {
    graph: createEmptyWorkflowDraftGraph(ctx),
    pendingWebhookCreates: new Map(),
    idempotencyKey: null,
  };
}

export function createEmptyWorkflowDraftGraph(
  _ctx: DraftGraphContext
): WorkflowGraphApiData {
  return {
    nodes: [],
    edges: [],
    triggerBindings: [],
    triggers: [],
  };
}

export const DEFAULT_NEW_WORKFLOW_NAME = 'Untitled workflow';

export function isWorkflowDraftDirty(
  draft: WorkflowDraftState,
  name: string
): boolean {
  const { nodes, edges, triggers, triggerBindings } = draft.graph;
  if (nodes.length > 0 || edges.length > 0 || triggers.length > 0) {
    return true;
  }
  if (triggerBindings.some((binding) => binding.enabled)) {
    return true;
  }
  if (name !== DEFAULT_NEW_WORKFLOW_NAME) {
    return true;
  }
  return false;
}

function nowIso(): string {
  return new Date().toISOString();
}

function makeDraftNode(
  ctx: DraftGraphContext,
  type: string,
  data: unknown,
  inputShape: string | null = null
): WorkflowGraphApiData['nodes'][number] {
  const timestamp = nowIso();
  return {
    id: v4(),
    ownerId: ctx.userId,
    ownerOrgId: ctx.orgId,
    workflowId: ctx.workflowId,
    type,
    data,
    inputShape,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

function makeDraftEdge(
  ctx: DraftGraphContext,
  fromNodeId: string,
  toNodeId: string
): WorkflowGraphApiData['edges'][number] {
  const timestamp = nowIso();
  return {
    id: v4(),
    ownerId: ctx.userId,
    ownerOrgId: ctx.orgId,
    workflowId: ctx.workflowId,
    fromNodeId,
    toNodeId,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

/**
 * Which upstream node(s) a newly added node should connect from. A single id
 * makes a linear chain; an array fans multiple upstream nodes into the new node
 * (one incoming edge each, which the run engine resolves as fan-in input). An
 * empty array or `undefined` adds the node free-floating.
 */
/**
 * How a newly added node wires into the graph.
 *
 * - a node id, or a list of them → the new node runs *after* those, one inbound
 *   edge per id (several ids fan in to it)
 * - `{ upstreamOf }` → the new node runs *before* that node, one outbound edge
 *   into it. Symmetric to the fan-in case: if the target already has inbound
 *   edges this fans in alongside them rather than splicing into the chain.
 * - `undefined` → unconnected; it becomes an entry node
 */
export type DraftConnectFrom =
  | string
  | readonly string[]
  | { readonly upstreamOf: string }
  | undefined;

export function isDraftConnectUpstream(
  connectFrom: DraftConnectFrom
): connectFrom is { readonly upstreamOf: string } {
  // Array check first — a `readonly string[]` is also a non-null object.
  return (
    typeof connectFrom === 'object' &&
    connectFrom !== null &&
    !Array.isArray(connectFrom)
  );
}

function normalizeConnectFrom(
  connectFrom: DraftConnectFrom
): readonly string[] {
  if (connectFrom === undefined || isDraftConnectUpstream(connectFrom)) {
    return [];
  }
  return typeof connectFrom === 'string' ? [connectFrom] : connectFrom;
}

function makeDraftBinding(
  ctx: DraftGraphContext,
  triggerType: string,
  triggerId: string
): WorkflowGraphApiData['triggerBindings'][number] {
  const timestamp = nowIso();
  return {
    id: v4(),
    ownerId: ctx.userId,
    ownerOrgId: ctx.orgId,
    triggerType,
    triggerId,
    workflowId: ctx.workflowId,
    enabled: PersistedWorkflow.triggerBindingEnabledByDefault(triggerType),
    priority: 0,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

export function draftAddAgentNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  agentId: string,
  agentName: string,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const node = makeDraftNode(ctx, 'agent', { agentId, name: agentName });
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

export function draftAddForEachNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const node = makeDraftNode(ctx, 'for-each', { itemsPath: 'items' });
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

export function draftAddFilterNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const node = makeDraftNode(ctx, 'filter', {
    path: '',
    operator: 'exists',
    value: '',
  });
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

/**
 * Seeds a debounce node with the SDK defaults for everything but the key, and
 * the viewer's own timezone rather than UTC — someone configuring "once a week"
 * in the editor means their week, and a boundary they cannot see is the one
 * thing about this node that is hard to debug later.
 */
export function draftAddDebounceNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const node = makeDraftNode(ctx, 'debounce', {
    keyPath: '',
    window: PersistedWorkflow.DEFAULT_DEBOUNCE_WINDOW,
    timezone: resolveViewerTimezone(),
    weekStartsOn: PersistedWorkflow.DEFAULT_DEBOUNCE_WEEK_START,
  });
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

export function draftAddEmitEventNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const node = makeDraftNode(ctx, 'emit-event', {
    eventName: '',
  });
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

/**
 * A mapper starts with no fields: it is configured entirely in its drawer, and
 * an empty one passes its input straight through until it has any.
 */
export function draftAddMapperNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const node = makeDraftNode(ctx, 'mapper', {});
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

/**
 * A bucket starts with a sensible default policy (flush every 100 items) so it
 * is immediately valid; the count/time triggers and reducers are configured in
 * its drawer.
 */
export function draftAddBucketNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const node = makeDraftNode(ctx, 'bucket', {
    policy: {
      combinator: 'or',
      triggers: [{ kind: 'count', threshold: 100 }],
    },
  });
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

/**
 * A group-by seeded with `/items`, because a bucket flush is what these read and
 * that is where its list lives. The key is left blank on purpose: it is the one
 * decision the node cannot guess, and a wrong default would group everything
 * under a single value while looking configured.
 *
 * When it is attached directly to a bucket, the bucket is also recorded as the
 * list's source, so the editor shows the qualified `Bucket.items` the picker
 * would have produced rather than a bare `/items` that reads as unattributed.
 */
export function draftAddGroupByNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const fromIds = normalizeConnectFrom(connectFrom);
  const fromNode =
    fromIds.length === 1
      ? state.graph.nodes.find((candidate) => candidate.id === fromIds[0])
      : undefined;
  const data: Record<string, unknown> = { itemsPath: '/items', keyPath: '' };
  if (fromNode?.type === 'bucket') {
    data['sourceRunNodeId'] = fromNode.id;
  }
  const node = makeDraftNode(ctx, 'group-by', data);
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

export function draftAddRunWorkflowNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  targetWorkflowId: string,
  workflowName: string,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const node = makeDraftNode(ctx, 'run-workflow', {
    targetWorkflowId,
    name: workflowName,
  });
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

export function draftAddToolCallNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  toolKey: string,
  toolName: string,
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const node = makeDraftNode(ctx, 'tool-call', {
    key: toolKey,
    params: {},
    name: toolName,
  });
  return draftAddNodeWithOptionalEdge(state, ctx, node, connectFrom);
}

/**
 * Adds a node as the for-each loop body: connects for-each → new node and
 * stamps `data.childNodeId` even when other outgoing (fan-in) edges already
 * exist. Replaces a previous body node when one is already stamped.
 */
export function draftAddForEachBodyNode(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  forEachNodeId: string,
  body: WorkflowGraphApiData['nodes'][number]
): WorkflowDraftState {
  const forEachNode = state.graph.nodes.find(
    (node) => node.id === forEachNodeId && node.type === 'for-each'
  );
  if (forEachNode === undefined) {
    return state;
  }

  const previousChildId = loopBodyNodeId(forEachNode);
  const withoutPrevious =
    previousChildId !== null && previousChildId !== body.id
      ? draftDeleteWorkflowNode(state, previousChildId)
      : state;

  const timestamp = nowIso();
  const nodes = withoutPrevious.graph.nodes.map((node) => {
    if (node.id !== forEachNodeId) {
      return node;
    }
    const data = asNodeDataRecord(node.data);
    data['childNodeId'] = body.id;
    data['bodyType'] = body.type;
    data['bodyName'] = forEachBodyDisplayName(body);
    return { ...node, data, updatedAt: timestamp };
  });

  return {
    ...withoutPrevious,
    graph: withForEachChildNodeIds({
      ...withoutPrevious.graph,
      nodes: [...nodes, body],
      edges: [
        ...withoutPrevious.graph.edges,
        makeDraftEdge(ctx, forEachNodeId, body.id),
      ],
    }),
  };
}

export function draftAddAgentAsForEachBody(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  forEachNodeId: string,
  agentId: string,
  agentName: string
): WorkflowDraftState {
  return draftAddForEachBodyNode(
    state,
    ctx,
    forEachNodeId,
    makeDraftNode(ctx, 'agent', { agentId, name: agentName })
  );
}

export function draftAddToolCallAsForEachBody(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  forEachNodeId: string,
  toolKey: string,
  toolName: string
): WorkflowDraftState {
  return draftAddForEachBodyNode(
    state,
    ctx,
    forEachNodeId,
    makeDraftNode(ctx, 'tool-call', {
      key: toolKey,
      params: {},
      name: toolName,
    })
  );
}

export function draftAddRunWorkflowAsForEachBody(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  forEachNodeId: string,
  targetWorkflowId: string,
  workflowName: string
): WorkflowDraftState {
  return draftAddForEachBodyNode(
    state,
    ctx,
    forEachNodeId,
    makeDraftNode(ctx, 'run-workflow', {
      targetWorkflowId,
      name: workflowName,
    })
  );
}

export function draftAddFilterAsForEachBody(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  forEachNodeId: string
): WorkflowDraftState {
  return draftAddForEachBodyNode(
    state,
    ctx,
    forEachNodeId,
    makeDraftNode(ctx, 'filter', {
      path: '',
      operator: 'exists',
      value: '',
    })
  );
}

/**
 * Debounce as a loop body: fan out over rows, then let each row's key through
 * once per window. The common shape for "one notification per thing per week"
 * over a collection.
 */
export function draftAddDebounceAsForEachBody(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  forEachNodeId: string
): WorkflowDraftState {
  return draftAddForEachBodyNode(
    state,
    ctx,
    forEachNodeId,
    makeDraftNode(ctx, 'debounce', {
      keyPath: '',
      window: PersistedWorkflow.DEFAULT_DEBOUNCE_WINDOW,
      timezone: resolveViewerTimezone(),
      weekStartsOn: PersistedWorkflow.DEFAULT_DEBOUNCE_WEEK_START,
    })
  );
}

/**
 * Adopts an EXISTING outgoing successor of a for-each as its loop body (moves it
 * "inside" the loop) by stamping `childNodeId`/`bodyType`/`bodyName`. Unlike
 * `draftAddForEachBodyNode` this creates no new node and deletes nothing — it
 * only re-labels a node that is already wired from the for-each. Use it to
 * repair graphs where a body node was left as a visible fan-in successor (e.g.
 * built by Coco or by dragging a raw edge).
 */
export function draftAdoptForEachBody(
  state: WorkflowDraftState,
  forEachNodeId: string,
  bodyNodeId: string
): WorkflowDraftState {
  const forEachNode = state.graph.nodes.find(
    (node) => node.id === forEachNodeId && node.type === 'for-each'
  );
  const body = state.graph.nodes.find((node) => node.id === bodyNodeId);
  const connected = state.graph.edges.some(
    (edge) => edge.fromNodeId === forEachNodeId && edge.toNodeId === bodyNodeId
  );
  if (forEachNode === undefined || body === undefined || !connected) {
    return state;
  }

  const timestamp = nowIso();
  const nodes = state.graph.nodes.map((node) => {
    if (node.id !== forEachNodeId) {
      return node;
    }
    const data = asNodeDataRecord(node.data);
    data['childNodeId'] = body.id;
    data['bodyType'] = body.type;
    data['bodyName'] = forEachBodyDisplayName(body);
    return { ...node, data, updatedAt: timestamp };
  });

  return {
    ...state,
    graph: withForEachChildNodeIds({ ...state.graph, nodes }),
  };
}

function asNodeDataRecord(data: unknown): Record<string, unknown> {
  if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
    return { ...(data as Record<string, unknown>) };
  }
  return {};
}

/** The node a loop runs per item; null for anything that doesn't loop. */
function loopBodyNodeId(
  node: WorkflowGraphApiData['nodes'][number]
): string | null {
  return PersistedWorkflow.workflowNodeFromGraphNode(node).loopBodyNodeId();
}

function forEachBodyDisplayName(
  body: WorkflowGraphApiData['nodes'][number]
): string {
  const data = asNodeDataRecord(body.data);
  const name = data['name'];
  if (typeof name === 'string' && name.trim() !== '') {
    return name;
  }
  if (body.type === 'tool-call') {
    const key = data['key'];
    if (typeof key === 'string' && key.trim() !== '') {
      return key;
    }
    return 'Tool call';
  }
  if (body.type === 'agent') {
    return 'Agent';
  }
  if (body.type === 'filter') {
    return 'Filter';
  }
  if (body.type === 'debounce') {
    return 'Debounce';
  }
  return body.type;
}

/**
 * Keeps for-each `data.childNodeId` valid against outgoing edges:
 * - preserve when the stamped body edge still exists
 * - clear when the body edge (or body node) is gone
 *
 * Does **not** auto-assign body from canvas edges — the loop body is chosen in
 * the for-each editor (and hidden on the canvas). Visible for-each outs are
 * fan-in continuations.
 */
function withForEachChildNodeIds(
  graph: WorkflowGraphApiData
): WorkflowGraphApiData {
  const outgoingByFrom = new Map<string, string[]>();
  for (const edge of graph.edges) {
    const existing = outgoingByFrom.get(edge.fromNodeId) ?? [];
    existing.push(edge.toNodeId);
    outgoingByFrom.set(edge.fromNodeId, existing);
  }

  let changed = false;
  const nodes = graph.nodes.map((node) => {
    const outgoing = outgoingByFrom.get(node.id) ?? [];
    const current = loopBodyNodeId(node);
    const nextChild =
      current !== null && outgoing.includes(current) ? current : null;

    if (nextChild === current) {
      return node;
    }
    changed = true;
    const data = asNodeDataRecord(node.data);
    if (nextChild === null) {
      delete data['childNodeId'];
      delete data['bodyType'];
      delete data['bodyName'];
    } else {
      data['childNodeId'] = nextChild;
    }
    return { ...node, data, updatedAt: nowIso() };
  });

  const nextGraph = changed ? { ...graph, nodes } : graph;
  return withForEachBodyDisplayFields(nextGraph);
}

function draftAddNodeWithOptionalEdge(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  node: WorkflowGraphApiData['nodes'][number],
  connectFrom?: DraftConnectFrom
): WorkflowDraftState {
  const newEdges = isDraftConnectUpstream(connectFrom)
    ? [makeDraftEdge(ctx, node.id, connectFrom.upstreamOf)]
    : normalizeConnectFrom(connectFrom).map((fromNodeId) =>
        makeDraftEdge(ctx, fromNodeId, node.id)
      );
  const edges =
    newEdges.length > 0
      ? [...state.graph.edges, ...newEdges]
      : state.graph.edges;
  return {
    ...state,
    graph: withForEachChildNodeIds({
      ...state.graph,
      nodes: [...state.graph.nodes, node],
      edges,
    }),
  };
}

/**
 * Rewrites node ids buried in a node's `data` to their pasted counterparts.
 *
 * Two shapes need it, and both are strings: a for-each's `childNodeId`, and the
 * `{{<nodeId>/pointer}}` refs a tool-call or agent prompt uses to read an
 * upstream step's output. Rather than teach this about every node type, it walks
 * the whole payload and swaps any occurrence of a copied id — safe because the
 * ids are UUIDs, which don't turn up inside prose by accident.
 *
 * Ids outside the copied set are left alone: they still point at the original
 * upstream node, which is the best available answer when you copy a step away
 * from what fed it.
 */
function remapNodeDataIds(
  data: unknown,
  idMap: ReadonlyMap<string, string>
): unknown {
  if (typeof data === 'string') {
    let next = data;
    for (const [oldId, newId] of idMap) {
      if (next.includes(oldId)) {
        next = next.split(oldId).join(newId);
      }
    }
    return next;
  }
  if (Array.isArray(data)) {
    return data.map((entry) => remapNodeDataIds(entry, idMap));
  }
  if (data !== null && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([key, value]) => [
        key,
        remapNodeDataIds(value, idMap),
      ])
    );
  }
  return data;
}

/** Trailing `copy` / `copy 3`, so duplicating a copy renumbers rather than stacks. */
const COPY_NAME_SUFFIX = /\s+copy(?:\s+\d+)?$/i;

/**
 * `Fetch orders` → `Fetch orders copy`, then `copy 2`, `copy 3`…
 *
 * A copy that reads exactly like its original is the other half of "where did my
 * duplicate go": even once you find both, nothing on the cards tells you which
 * one you just made.
 */
function nextCopyName(name: string, taken: ReadonlySet<string>): string {
  const base = name.replace(COPY_NAME_SUFFIX, '').trim();
  const root = base === '' ? name.trim() : base;
  let candidate = `${root} copy`;
  let attempt = 2;
  while (taken.has(candidate)) {
    candidate = `${root} copy ${attempt}`;
    attempt += 1;
  }
  return candidate;
}

function readNodeName(data: unknown): string | undefined {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return undefined;
  }
  const name = (data as Record<string, unknown>)['name'];
  if (typeof name !== 'string' || name.trim() === '') {
    return undefined;
  }
  return name;
}

/** Renames a copied node's `data.name`, reserving the new name in `taken`. */
function withCopiedNodeName(data: unknown, taken: Set<string>): unknown {
  const name = readNodeName(data);
  if (name === undefined) {
    return data;
  }
  const copyName = nextCopyName(name, taken);
  taken.add(copyName);
  return { ...(data as Record<string, unknown>), name: copyName };
}

/**
 * Where the pasted copies attach to the graph they land in.
 *
 * - `source-inputs` → whatever fed the originals now also feeds the copies, so a
 *   copy sits as a sibling right beside the node it came from. Falls back to
 *   `detached` when those upstream nodes aren't in this graph.
 * - `anchor-node` → the copies run after one named node. An explicit "put it
 *   here" from the canvas selection.
 * - `detached` → no incoming edges, which makes every root of the slice an entry
 *   node. Only correct when the originals were entry nodes too.
 */
export type WorkflowPasteAnchor =
  | { readonly kind: 'source-inputs' }
  | { readonly kind: 'anchor-node'; readonly nodeId: string }
  | { readonly kind: 'detached' };

/** Copied nodes with no inbound edge from *within* the slice — where it starts. */
function payloadRootNodeIds(payload: WorkflowClipboardPayload): string[] {
  const internalTargets = new Set(payload.edges.map((edge) => edge.toNodeId));
  return payload.nodes
    .map((node) => node.id)
    .filter((id) => !internalTargets.has(id));
}

function resolvePasteAnchorEdges(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  payload: WorkflowClipboardPayload,
  idMap: ReadonlyMap<string, string>,
  anchor: WorkflowPasteAnchor
): WorkflowGraphApiData['edges'] {
  if (anchor.kind === 'detached') {
    return [];
  }

  const existingIds = new Set(state.graph.nodes.map((node) => node.id));

  if (anchor.kind === 'anchor-node') {
    if (!existingIds.has(anchor.nodeId)) {
      return [];
    }
    return payloadRootNodeIds(payload).flatMap((rootId) => {
      const toNodeId = idMap.get(rootId);
      if (toNodeId === undefined) {
        return [];
      }
      return [makeDraftEdge(ctx, anchor.nodeId, toNodeId)];
    });
  }

  // Same upstream nodes as the originals. Deduped because two copied nodes can
  // share a parent, and a parent feeding the same copy twice is not a fan-in.
  const seen = new Set<string>();
  const edges: WorkflowGraphApiData['edges'] = [];
  for (const edge of payload.inboundEdges) {
    const toNodeId = idMap.get(edge.toNodeId);
    if (toNodeId === undefined || !existingIds.has(edge.fromNodeId)) {
      continue;
    }
    const key = `${edge.fromNodeId}:${toNodeId}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    edges.push(makeDraftEdge(ctx, edge.fromNodeId, toNodeId));
  }
  return edges;
}

/**
 * Pastes a copied slice of the graph as brand-new nodes.
 *
 * Two things beyond minting ids, both there so the copies are findable. They
 * attach where `anchor` says rather than floating — canvas positions aren't
 * persisted (dagre re-lays the graph out on every structural change), so an
 * edge is the only way to say "put it next to that one", and a floating node is
 * also a live entry point rather than an inert one. And their names get a `copy`
 * suffix, so the two cards can be told apart once found.
 *
 * `attached` reports whether anything actually wired up, so a caller can say so
 * when copies land loose in a graph that already has a starting point.
 */
export function draftPasteWorkflowNodes(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  payload: WorkflowClipboardPayload,
  anchor: WorkflowPasteAnchor = { kind: 'source-inputs' }
): { state: WorkflowDraftState; nodeIds: string[]; attached: boolean } {
  if (payload.nodes.length === 0) {
    return { state, nodeIds: [], attached: false };
  }

  const idMap = new Map(payload.nodes.map((node) => [node.id, v4()] as const));
  const timestamp = nowIso();
  const takenNames = new Set(
    state.graph.nodes
      .map((node) => readNodeName(node.data))
      .filter((name): name is string => name !== undefined)
  );

  const nodes = payload.nodes.map((node) => {
    const id = idMap.get(node.id);
    // Every id in the payload seeded the map above.
    return {
      ...node,
      id: id ?? v4(),
      ownerId: ctx.userId,
      ownerOrgId: ctx.orgId,
      workflowId: ctx.workflowId,
      data: withCopiedNodeName(remapNodeDataIds(node.data, idMap), takenNames),
      createdAt: timestamp,
      updatedAt: timestamp,
    };
  });

  const internalEdges = payload.edges.flatMap((edge) => {
    const fromNodeId = idMap.get(edge.fromNodeId);
    const toNodeId = idMap.get(edge.toNodeId);
    if (fromNodeId === undefined || toNodeId === undefined) {
      return [];
    }
    return [makeDraftEdge(ctx, fromNodeId, toNodeId)];
  });

  const anchorEdges = resolvePasteAnchorEdges(
    state,
    ctx,
    payload,
    idMap,
    anchor
  );

  return {
    state: {
      ...state,
      graph: withForEachChildNodeIds({
        ...state.graph,
        nodes: [...state.graph.nodes, ...nodes],
        edges: [...state.graph.edges, ...internalEdges, ...anchorEdges],
      }),
    },
    nodeIds: nodes.map((node) => node.id),
    attached: anchorEdges.length > 0,
  };
}

export function draftUpdateNodeData(
  state: WorkflowDraftState,
  nodeId: string,
  node: WorkflowNodeInput
): WorkflowDraftState {
  const inputShape =
    'inputShape' in node && typeof node.inputShape === 'string'
      ? node.inputShape
      : null;
  // Returning the same state for an unchanged payload is load-bearing: the
  // `updatedAt` stamp below lands in the draft/baseline comparison, so a no-op
  // update would mark the workflow dirty and re-trigger autosave. Autosaving
  // node editors report their current value on mount, so opening a config form
  // without touching it is exactly that no-op.
  //
  // The comparison has to be key-order insensitive — editors rebuild `data`
  // from their own field order, which need not match the order the node was
  // seeded or hydrated with.
  const current = state.graph.nodes.find((existing) => existing.id === nodeId);
  if (
    current !== undefined &&
    current.type === node.type &&
    current.inputShape === inputShape &&
    stableStringify(current.data) === stableStringify(node.data)
  ) {
    return state;
  }
  return {
    ...state,
    graph: {
      ...state.graph,
      nodes: state.graph.nodes.map((existing) =>
        existing.id === nodeId
          ? {
              ...existing,
              type: node.type,
              data: node.data,
              inputShape,
              updatedAt: nowIso(),
            }
          : existing
      ),
    },
  };
}

export function draftDeleteWorkflowNode(
  state: WorkflowDraftState,
  nodeId: string
): WorkflowDraftState {
  return {
    ...state,
    graph: withForEachChildNodeIds({
      ...state.graph,
      nodes: state.graph.nodes.filter((node) => node.id !== nodeId),
      edges: state.graph.edges.filter(
        (edge) => edge.fromNodeId !== nodeId && edge.toNodeId !== nodeId
      ),
    }),
  };
}

export function draftAddWorkflowEdge(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  fromNodeId: string,
  toNodeId: string
): WorkflowDraftState {
  const exists = state.graph.edges.some(
    (edge) => edge.fromNodeId === fromNodeId && edge.toNodeId === toNodeId
  );
  if (exists) {
    return state;
  }
  return {
    ...state,
    graph: withForEachChildNodeIds({
      ...state.graph,
      edges: [...state.graph.edges, makeDraftEdge(ctx, fromNodeId, toNodeId)],
    }),
  };
}

export function draftDeleteWorkflowEdge(
  state: WorkflowDraftState,
  edgeId: string
): WorkflowDraftState {
  return {
    ...state,
    graph: withForEachChildNodeIds({
      ...state.graph,
      edges: state.graph.edges.filter((edge) => edge.id !== edgeId),
    }),
  };
}

export function draftUpdateWorkflowEdge(
  state: WorkflowDraftState,
  edgeId: string,
  update: { fromNodeId?: string; toNodeId?: string }
): WorkflowDraftState {
  const edge = state.graph.edges.find((row) => row.id === edgeId);
  if (edge === undefined) {
    return state;
  }

  const fromNodeId = update.fromNodeId ?? edge.fromNodeId;
  const toNodeId = update.toNodeId ?? edge.toNodeId;
  if (fromNodeId === toNodeId) {
    return state;
  }

  const duplicate = state.graph.edges.some(
    (row) =>
      row.id !== edgeId &&
      row.fromNodeId === fromNodeId &&
      row.toNodeId === toNodeId
  );
  if (duplicate) {
    return state;
  }

  return {
    ...state,
    graph: withForEachChildNodeIds({
      ...state.graph,
      edges: state.graph.edges.map((row) =>
        row.id === edgeId
          ? {
              ...row,
              fromNodeId,
              toNodeId,
              updatedAt: nowIso(),
            }
          : row
      ),
    }),
  };
}

export function draftAddCronTrigger(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  schedule: string,
  timezone: string,
  name: string
): WorkflowDraftState {
  const triggerId = v4();
  const timestamp = nowIso();
  const graph = state.graph;
  const binding = makeDraftBinding(
    ctx,
    PersistedWorkflowTriggerKind.Cron,
    triggerId
  );
  return {
    ...state,
    graph: {
      ...graph,
      triggerBindings: [...graph.triggerBindings, binding],
      triggers: [
        ...graph.triggers,
        {
          kind: PersistedWorkflowTriggerKind.Cron,
          trigger: {
            id: triggerId,
            ownerId: ctx.userId,
            ownerOrgId: ctx.orgId,
            name,
            cron: schedule,
            timezone,
            workflowInput: {},
            enabled: true,
            createdAt: timestamp,
            updatedAt: timestamp,
          },
        },
      ],
    },
  };
}

export function draftAddEventTrigger(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  name: string,
  eventName: string,
  triggerId = v4()
): { state: WorkflowDraftState; triggerId: string } {
  const timestamp = nowIso();
  const graph = state.graph;
  const binding = makeDraftBinding(
    ctx,
    PersistedWorkflowTriggerKind.Event,
    triggerId
  );
  return {
    state: {
      ...state,
      graph: {
        ...graph,
        triggerBindings: [...graph.triggerBindings, binding],
        triggers: [
          ...graph.triggers,
          {
            kind: PersistedWorkflowTriggerKind.Event,
            trigger: {
              id: triggerId,
              ownerId: ctx.userId,
              ownerOrgId: ctx.orgId,
              name,
              eventName,
              enabled: true,
              createdAt: timestamp,
              updatedAt: timestamp,
            },
          },
        ],
      },
    },
    triggerId,
  };
}

export function draftAddDatasetTrigger(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  params: {
    name: string;
    datasetId: string;
    criterion: DatasetTriggerCriterion;
    trackFields: string[] | null;
    filter: unknown;
    processExistingRows: boolean;
    runMode: DatasetTriggerRunMode;
  },
  triggerId = v4()
): { state: WorkflowDraftState; triggerId: string } {
  const timestamp = nowIso();
  const graph = state.graph;
  const binding = makeDraftBinding(
    ctx,
    PersistedWorkflowTriggerKind.Dataset,
    triggerId
  );
  return {
    state: {
      ...state,
      graph: {
        ...graph,
        triggerBindings: [...graph.triggerBindings, binding],
        triggers: [
          ...graph.triggers,
          {
            kind: PersistedWorkflowTriggerKind.Dataset,
            trigger: {
              id: triggerId,
              ownerId: ctx.userId,
              ownerOrgId: ctx.orgId,
              name: params.name,
              datasetId: params.datasetId,
              criterion: params.criterion,
              trackFields: params.trackFields,
              filter: params.filter,
              processExistingRows: params.processExistingRows,
              runMode: params.runMode,
              enabled: true,
              createdAt: timestamp,
              updatedAt: timestamp,
            },
          },
        ],
      },
    },
    triggerId,
  };
}

export function draftBindExistingTrigger(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  trigger: WorkflowTriggerOption
): WorkflowDraftState {
  const graph = state.graph;
  const existingBinding = graph.triggerBindings.find(
    (entry) => entry.triggerId === trigger.id
  );
  // Re-adding a trigger the workflow already has switches it back on — except a
  // webhook, which is only ever switched on from its own card.
  const binding =
    existingBinding === undefined
      ? makeDraftBinding(ctx, trigger.kind, trigger.id)
      : PersistedWorkflow.triggerBindingEnabledByDefault(trigger.kind)
      ? { ...existingBinding, enabled: true, updatedAt: nowIso() }
      : existingBinding;
  const timestamp = nowIso();
  const snapshot =
    trigger.kind === PersistedWorkflowTriggerKind.Cron
      ? ({
          kind: PersistedWorkflowTriggerKind.Cron,
          trigger: {
            id: trigger.id,
            ownerId: ctx.userId,
            ownerOrgId: ctx.orgId,
            name: trigger.name,
            cron: trigger.schedule ?? '0 9 * * *',
            timezone: 'UTC',
            workflowInput: {},
            enabled: true,
            createdAt: timestamp,
            updatedAt: timestamp,
          },
        } satisfies PersistedWorkflow.PersistedWorkflowTriggerSnapshot)
      : trigger.kind === PersistedWorkflowTriggerKind.Event
      ? ({
          kind: PersistedWorkflowTriggerKind.Event,
          trigger: {
            id: trigger.id,
            ownerId: ctx.userId,
            ownerOrgId: ctx.orgId,
            name: trigger.name,
            eventName:
              trigger.eventName !== undefined &&
              trigger.eventName !== null &&
              trigger.eventName !== ''
                ? trigger.eventName
                : trigger.name,
            enabled: true,
            createdAt: timestamp,
            updatedAt: timestamp,
          },
        } satisfies PersistedWorkflow.PersistedWorkflowTriggerSnapshot)
      : trigger.kind === PersistedWorkflowTriggerKind.Dataset
      ? ({
          kind: PersistedWorkflowTriggerKind.Dataset,
          trigger: {
            id: trigger.id,
            ownerId: ctx.userId,
            ownerOrgId: ctx.orgId,
            name: trigger.name,
            datasetId: trigger.datasetId ?? '',
            criterion: trigger.criterion ?? 'new_row',
            trackFields: null,
            filter: null,
            processExistingRows: false,
            runMode: trigger.runMode ?? 'automatic',
            enabled: true,
            createdAt: timestamp,
            updatedAt: timestamp,
          },
        } satisfies PersistedWorkflow.PersistedWorkflowTriggerSnapshot)
      : // Webhook is the fall-through, so an unrecognized kind lands here rather
        // than failing. Add an explicit branch above when adding a kind.
        ({
          kind: PersistedWorkflowTriggerKind.Webhook,
          trigger: {
            id: trigger.id,
            ownerId: ctx.userId,
            ownerOrgId: ctx.orgId,
            name: trigger.name,
            secret: '',
            enabled: true,
            installStatus: 'active',
            createdAt: timestamp,
            updatedAt: timestamp,
          },
        } as PersistedWorkflow.PersistedWorkflowTriggerSnapshot);

  return {
    ...state,
    graph: {
      ...graph,
      triggerBindings:
        existingBinding === undefined
          ? [...graph.triggerBindings, binding]
          : graph.triggerBindings.map((entry) =>
              entry.id === binding.id ? binding : entry
            ),
      triggers: graph.triggers.some((entry) => entry.trigger.id === trigger.id)
        ? graph.triggers.map((entry) =>
            entry.trigger.id === trigger.id ? snapshot : entry
          )
        : [...graph.triggers, snapshot],
    },
  };
}

export function isDraftOnlyWebhookTrigger(
  state: WorkflowDraftState,
  triggerId: string
): boolean {
  return state.pendingWebhookCreates.has(triggerId);
}

/** Triggers on an unsaved workflow only exist in local draft state. */
export function isDraftOnlyTriggerInWorkflow(
  state: WorkflowDraftState,
  triggerId: string,
  isNewWorkflow: boolean
): boolean {
  if (isNewWorkflow) {
    return true;
  }
  return isDraftOnlyWebhookTrigger(state, triggerId);
}

export function draftAddWebhookTrigger(
  state: WorkflowDraftState,
  ctx: DraftGraphContext,
  params: DraftWebhookCreateParams
): { state: WorkflowDraftState; triggerId: string; secret: string } {
  const triggerId = v4();
  const timestamp = nowIso();
  const graph = state.graph;
  const binding = makeDraftBinding(
    ctx,
    PersistedWorkflowTriggerKind.Webhook,
    triggerId
  );
  const pendingWebhookCreates = new Map(state.pendingWebhookCreates);
  pendingWebhookCreates.set(triggerId, params);
  return {
    triggerId,
    secret: 'save-workflow-to-reveal-secret',
    state: {
      graph: {
        ...graph,
        triggerBindings: [...graph.triggerBindings, binding],
        triggers: [
          ...graph.triggers,
          {
            kind: PersistedWorkflowTriggerKind.Webhook,
            trigger: {
              id: triggerId,
              ownerId: ctx.userId,
              ownerOrgId: ctx.orgId,
              name: params.name,
              secret: '',
              enabled: true,
              providerKey: params.providerKey,
              eventKey: params.eventKey,
              eventConfig: params.eventConfig,
              installStatus: 'draft',
              createdAt: timestamp,
              updatedAt: timestamp,
            },
          },
        ],
      },
      pendingWebhookCreates,
      idempotencyKey: state.idempotencyKey,
    },
  };
}

export function draftUpdateCronTrigger(
  state: WorkflowDraftState,
  params: {
    bindingId: string;
    name?: string;
    schedule?: string;
    timezone?: string;
    cronRowEnabled?: boolean;
  }
): WorkflowDraftState {
  const binding = state.graph.triggerBindings.find(
    (entry) => entry.id === params.bindingId
  );
  if (binding === undefined) {
    return state;
  }

  return {
    ...state,
    graph: {
      ...state.graph,
      triggers: state.graph.triggers.map((snapshot) => {
        if (snapshot.trigger.id !== binding.triggerId) {
          return snapshot;
        }
        if (snapshot.kind !== PersistedWorkflowTriggerKind.Cron) {
          return snapshot;
        }
        return {
          ...snapshot,
          trigger: {
            ...snapshot.trigger,
            ...(params.name !== undefined && { name: params.name }),
            ...(params.schedule !== undefined && { cron: params.schedule }),
            ...(params.timezone !== undefined && {
              timezone: params.timezone,
            }),
            ...(params.cronRowEnabled !== undefined && {
              enabled: params.cronRowEnabled,
            }),
            updatedAt: nowIso(),
          },
        };
      }),
    },
  };
}

export function draftUpdateEventTrigger(
  state: WorkflowDraftState,
  params: {
    bindingId: string;
    name?: string;
    eventName?: string;
    eventRowEnabled?: boolean;
  }
): WorkflowDraftState {
  const binding = state.graph.triggerBindings.find(
    (entry) => entry.id === params.bindingId
  );
  if (binding === undefined) {
    return state;
  }

  return {
    ...state,
    graph: {
      ...state.graph,
      triggers: state.graph.triggers.map((snapshot) => {
        if (snapshot.trigger.id !== binding.triggerId) {
          return snapshot;
        }
        if (snapshot.kind !== PersistedWorkflowTriggerKind.Event) {
          return snapshot;
        }
        return {
          ...snapshot,
          trigger: {
            ...snapshot.trigger,
            ...(params.name !== undefined && { name: params.name }),
            ...(params.eventName !== undefined && {
              eventName: params.eventName,
            }),
            ...(params.eventRowEnabled !== undefined && {
              enabled: params.eventRowEnabled,
            }),
            updatedAt: nowIso(),
          },
        };
      }),
    },
  };
}

export function draftUpdateDatasetTrigger(
  state: WorkflowDraftState,
  params: {
    bindingId: string;
    runMode?: DatasetTriggerRunMode;
    datasetRowEnabled?: boolean;
  }
): WorkflowDraftState {
  const binding = state.graph.triggerBindings.find(
    (entry) => entry.id === params.bindingId
  );
  if (binding === undefined) {
    return state;
  }

  return {
    ...state,
    graph: {
      ...state.graph,
      triggers: state.graph.triggers.map((snapshot) => {
        if (snapshot.trigger.id !== binding.triggerId) {
          return snapshot;
        }
        if (snapshot.kind !== PersistedWorkflowTriggerKind.Dataset) {
          return snapshot;
        }
        return {
          ...snapshot,
          trigger: {
            ...snapshot.trigger,
            ...(params.runMode !== undefined && { runMode: params.runMode }),
            ...(params.datasetRowEnabled !== undefined && {
              enabled: params.datasetRowEnabled,
            }),
            updatedAt: nowIso(),
          },
        };
      }),
    },
  };
}

export function draftUpdateWebhookTrigger(
  state: WorkflowDraftState,
  params: {
    bindingId: string;
    name?: string;
    webhookRowEnabled?: boolean;
    providerKey?: WorkflowWebhookProviderKey;
    verificationConfig?: unknown;
    eventKey?: string;
    eventConfig?: unknown;
    endpointSecret?: string;
  }
): WorkflowDraftState {
  const binding = state.graph.triggerBindings.find(
    (entry) => entry.id === params.bindingId
  );
  if (binding === undefined) {
    return state;
  }

  const pendingCreate = state.pendingWebhookCreates.get(binding.triggerId);
  if (pendingCreate !== undefined) {
    const pendingWebhookCreates = new Map(state.pendingWebhookCreates);
    pendingWebhookCreates.set(binding.triggerId, {
      ...pendingCreate,
      ...(params.name !== undefined && { name: params.name }),
      ...(params.providerKey !== undefined && {
        providerKey: params.providerKey,
      }),
      ...(params.verificationConfig !== undefined && {
        verificationConfig: params.verificationConfig,
      }),
      ...(params.eventKey !== undefined && { eventKey: params.eventKey }),
      ...(params.eventConfig !== undefined && {
        eventConfig: params.eventConfig,
      }),
      ...(params.endpointSecret !== undefined && {
        endpointSecret: params.endpointSecret,
      }),
    });
    return {
      ...state,
      pendingWebhookCreates,
      graph: {
        ...state.graph,
        triggers: state.graph.triggers.map((snapshot) => {
          if (snapshot.trigger.id !== binding.triggerId) {
            return snapshot;
          }
          if (snapshot.kind !== PersistedWorkflowTriggerKind.Webhook) {
            return snapshot;
          }
          return {
            ...snapshot,
            trigger: {
              ...snapshot.trigger,
              ...(params.name !== undefined && { name: params.name }),
              ...(params.webhookRowEnabled !== undefined && {
                enabled: params.webhookRowEnabled,
              }),
              ...(params.providerKey !== undefined && {
                providerKey: params.providerKey,
              }),
              ...(params.eventKey !== undefined && {
                eventKey: params.eventKey,
              }),
              ...(params.eventConfig !== undefined && {
                eventConfig: params.eventConfig,
              }),
              ...(params.endpointSecret !== undefined && {
                secret: params.endpointSecret,
              }),
              updatedAt: nowIso(),
            },
          };
        }),
      },
    };
  }

  return {
    ...state,
    graph: {
      ...state.graph,
      triggers: state.graph.triggers.map((snapshot) => {
        if (snapshot.trigger.id !== binding.triggerId) {
          return snapshot;
        }
        if (snapshot.kind !== PersistedWorkflowTriggerKind.Webhook) {
          return snapshot;
        }
        return {
          ...snapshot,
          trigger: {
            ...snapshot.trigger,
            ...(params.name !== undefined && { name: params.name }),
            ...(params.webhookRowEnabled !== undefined && {
              enabled: params.webhookRowEnabled,
            }),
            ...(params.providerKey !== undefined && {
              providerKey: params.providerKey,
            }),
            ...(params.verificationConfig !== undefined && {
              verificationConfig: params.verificationConfig,
            }),
            ...(params.eventKey !== undefined && { eventKey: params.eventKey }),
            ...(params.eventConfig !== undefined && {
              eventConfig: params.eventConfig,
            }),
            ...(params.endpointSecret !== undefined && {
              secret: params.endpointSecret,
            }),
            updatedAt: nowIso(),
          },
        };
      }),
    },
  };
}

/** Patches live webhook install state into the local draft graph (canvas + preview models). */
export function draftPatchWebhookTriggerInstallState(
  state: WorkflowDraftState,
  triggerId: string,
  install: {
    installStatus: WebhookInstallStatus;
    installError: { code?: string; message?: string } | null;
  }
): WorkflowDraftState | null {
  let changed = false;
  const triggers = state.graph.triggers.map((snapshot) => {
    if (snapshot.kind !== PersistedWorkflowTriggerKind.Webhook) {
      return snapshot;
    }
    if (
      snapshot.trigger.id !== triggerId ||
      snapshot.trigger.installStatus === install.installStatus
    ) {
      return snapshot;
    }
    changed = true;
    return {
      ...snapshot,
      trigger: { ...snapshot.trigger, installStatus: install.installStatus },
    };
  });
  if (!changed) {
    return null;
  }
  return {
    ...state,
    graph: {
      ...state.graph,
      triggers,
    },
  };
}

/**
 * Lifts a trigger row that is switched off.
 *
 * Generic in the snapshot so the spread keeps the caller's exact variant: a
 * plain `PersistedWorkflowTriggerSnapshot` parameter would widen `trigger` to
 * the union of all four serialized shapes and stop matching the branch its
 * `kind` names.
 */
function withTriggerRowEnabled<
  T extends PersistedWorkflow.PersistedWorkflowTriggerSnapshot
>(snapshot: T, timestamp: string): T {
  return snapshot.trigger.enabled
    ? snapshot
    : {
        ...snapshot,
        trigger: { ...snapshot.trigger, enabled: true, updatedAt: timestamp },
      };
}

/**
 * The canvas card's on/off switch: pauses (or resumes) one workflow ↔ trigger
 * binding.
 *
 * Scoped to the binding rather than the trigger row on purpose. Cron, webhook
 * and event rows are deduped org-wide on their identity key, so two workflows
 * on the same schedule share a single `workflow_triggers_cron` row — switching
 * *that* off would silently stop the other workflow too.
 *
 * Switching on also lifts a disabled trigger row, because "on" has to mean the
 * workflow will actually fire: dispatch requires `binding.enabled` AND the
 * row's own `enabled`, so leaving a dead row behind would give you a card that
 * reads on and never runs.
 */
export function draftSetTriggerBindingEnabled(
  state: WorkflowDraftState,
  params: { bindingId: string; enabled: boolean }
): WorkflowDraftState {
  const binding = state.graph.triggerBindings.find(
    (entry) => entry.id === params.bindingId
  );
  if (binding === undefined || binding.enabled === params.enabled) {
    return state;
  }
  const timestamp = nowIso();
  return {
    ...state,
    graph: {
      ...state.graph,
      triggerBindings: state.graph.triggerBindings.map((entry) =>
        entry.id === params.bindingId
          ? { ...entry, enabled: params.enabled, updatedAt: timestamp }
          : entry
      ),
      triggers:
        params.enabled === false
          ? state.graph.triggers
          : state.graph.triggers.map((snapshot) =>
              snapshot.trigger.id === binding.triggerId
                ? withTriggerRowEnabled(snapshot, timestamp)
                : snapshot
            ),
    },
  };
}

export function draftArchiveTriggerBinding(
  state: WorkflowDraftState,
  bindingId: string
): WorkflowDraftState {
  const binding = state.graph.triggerBindings.find(
    (entry) => entry.id === bindingId
  );
  const pendingWebhookCreates = new Map(state.pendingWebhookCreates);
  if (binding !== undefined) {
    pendingWebhookCreates.delete(binding.triggerId);
  }
  return {
    ...state,
    pendingWebhookCreates,
    graph: {
      ...state.graph,
      triggerBindings: state.graph.triggerBindings.filter(
        (entry) => entry.id !== bindingId
      ),
      triggers:
        binding === undefined
          ? state.graph.triggers
          : state.graph.triggers.filter(
              (snapshot) => snapshot.trigger.id !== binding.triggerId
            ),
    },
  };
}
